import { useEffect, useState } from "react";

import { type AdhdVariant, api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { mealChildProfilesEnabled } from "../lib/flags";
import { LANGS, LANG_LABEL, useLang } from "../lib/i18n";
import { adhdCopy } from "../lib/i18n/adhd";
import { type AppCopy, appCopy } from "../lib/i18n/app";
import { childrenCopy } from "../lib/i18n/children";
import { useProfile } from "../lib/profile-context";
import { ScreenShell } from "./screen-shell";
import { IconChevR } from "./ui/icon";
import { Eyebrow, H1, MonoMeta, TopBar } from "./ui/primitives";

type Member = {
  userId: string;
  email: string;
  name: string | null;
  role: string;
  joinedAt: number;
};

type Props = {
  householdId: string;
  onBack?: () => void;
  onAccountDeleted?: () => void;
  onOpenPrivacy?: () => void;
  onOpenTerms?: () => void;
  onOpenTargets?: () => void;
  onOpenAdmin?: () => void;
  onOpenChildren?: () => void;
};

function memberInitial(m: Member): string {
  const source = m.name?.trim() || m.email;
  return source.charAt(0).toUpperCase();
}

function relativeJoined(ts: number, t: AppCopy): string {
  const days = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  if (days <= 0) return t.dashRelToday;
  if (days === 1) return t.dashRelYesterday;
  if (days < 30) return t.dashRelDays(days);
  if (days < 365) return t.dashRelMonths(Math.floor(days / 30));
  return t.dashRelYears(Math.floor(days / 365));
}

export function DashboardScreen({
  householdId,
  onBack,
  onAccountDeleted,
  onOpenPrivacy,
  onOpenTerms,
  onOpenTargets,
  onOpenAdmin,
  onOpenChildren,
}: Props) {
  const { state, signOut, updateUser } = useAuth();
  const { lang, setLang } = useLang();
  const t = appCopy[lang];
  const tAdhd = adhdCopy[lang];
  const tChildren = childrenCopy[lang];
  const { children: childProfiles } = useProfile();
  const childProfilesFlagOn = mealChildProfilesEnabled();
  const authUser = state.status === "signed-in" ? state.user : null;
  const [name, setName] = useState<string>("");
  const [members, setMembers] = useState<Member[]>([]);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [resending, setResending] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .adminPing()
      .then((res) => {
        if (!cancelled) setIsAdmin(res.admin);
      })
      .catch(() => {
        // 403 = not admin, silent.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const user = state.status === "signed-in" ? state.user : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getCurrentHousehold(householdId)
      .then((res) => {
        if (cancelled) return;
        setName(res.name);
        setMembers(res.members);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t.dashLoadFailed);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, t.dashLoadFailed]);

  async function handleExport() {
    setExporting(true);
    setAccountError(null);
    try {
      await api.exportMyData();
    } catch (err) {
      setAccountError(err instanceof Error ? err.message : t.dashExportFailed);
    } finally {
      setExporting(false);
    }
  }

  async function handleDeleteAccount() {
    const confirmed = window.prompt(t.dashDeletePrompt);
    if (confirmed !== t.dashDeleteConfirmWord) return;
    setDeleting(true);
    setAccountError(null);
    try {
      await api.deleteMyAccount();
      if (onAccountDeleted) onAccountDeleted();
      else window.location.href = "/";
    } catch (err) {
      setAccountError(err instanceof Error ? err.message : t.dashDeleteFailed);
    } finally {
      setDeleting(false);
    }
  }

  async function handleInvite() {
    setGenerating(true);
    setError(null);
    try {
      const invite = await api.createInvite(householdId);
      setInviteCode(invite.code);
      setCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.dashInviteFailed);
    } finally {
      setGenerating(false);
    }
  }

  async function handleResendVerification() {
    setResending(true);
    setVerifyMsg(null);
    try {
      await api.resendVerification();
      setVerifyMsg(t.dashVerifySent);
    } catch (err) {
      setVerifyMsg(err instanceof Error ? err.message : t.dashSendFailed);
    } finally {
      setResending(false);
    }
  }

  const inviteUrl =
    inviteCode && typeof window !== "undefined"
      ? `${window.location.origin}/invite/${inviteCode}`
      : null;
  const inviteHostShort = inviteUrl ? inviteUrl.replace(/^https?:\/\//, "") : null;

  async function copyInvite() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard?.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  const showVerifyBanner = user && !user.emailVerified;
  const memberCount = members.length;

  return (
    <ScreenShell hideHeader>
      <div style={{ marginBottom: 18 }}>
        <TopBar
          {...(onBack ? { back: t.backJournal, onBack } : {})}
          title={t.dashTopTitle}
          right={
            <button
              type="button"
              onClick={() => {
                signOut().catch(() => null);
              }}
              style={{
                background: "none",
                border: "none",
                color: "var(--accent)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                padding: 0,
              }}
            >
              {t.dashSignOut}
            </button>
          }
        />
      </div>

      <H1 accent=".">{loading ? t.dashLoading : name || t.dashYourHome}</H1>
      <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
        <span
          className="tabular"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            color: "var(--ink)",
          }}
        >
          {t.dashMemberCount(memberCount)}
        </span>{" "}
        · {t.dashSharedTagline}
      </p>

      {showVerifyBanner ? (
        <div
          className="rounded-lg p-3 flex flex-col gap-1"
          style={{ background: "var(--warn-bg)", color: "var(--warn-fg)", marginTop: 14 }}
        >
          <p style={{ fontSize: 13 }}>{t.dashVerifyEmail(user?.email ?? "")}</p>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resending}
            className="text-[11px] underline self-start"
            style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }}
          >
            {resending ? t.dashVerifyResending : t.dashVerifyResend}
          </button>
          {verifyMsg ? <p style={{ fontSize: 11 }}>{verifyMsg}</p> : null}
        </div>
      ) : null}

      {/* Members */}
      <section style={{ marginTop: 24 }}>
        <Eyebrow>{t.dashMembersEyebrow}</Eyebrow>
        <div style={{ marginTop: 10 }}>
          {loading ? (
            <p style={{ fontSize: 13, color: "var(--muted)" }}>{t.dashMembersLoading}</p>
          ) : members.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--muted)" }}>{t.dashMembersEmpty}</p>
          ) : (
            members.map((m) => {
              const isYou = user?.id === m.userId;
              const isAdmin = m.role === "admin";
              return (
                <div
                  key={m.userId}
                  style={{
                    padding: "14px 0",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    borderTop: "1px solid var(--nav-border)",
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 99,
                      background: isAdmin ? "var(--accent)" : "var(--ink)",
                      color: "#fff",
                      fontSize: 14,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {memberInitial(m)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
                    >
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "var(--ink)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.name ?? m.email}
                        {isYou ? t.dashYouSuffix : ""}
                      </span>
                      {isAdmin ? (
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 9,
                            padding: "2px 6px",
                            borderRadius: 3,
                            background: "var(--accent)",
                            color: "var(--accent-ink)",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                          }}
                        >
                          {t.dashAdminBadge}
                        </span>
                      ) : null}
                    </div>
                    <MonoMeta>
                      {t.dashJoinedPrefix}
                      {relativeJoined(m.joinedAt, t).toUpperCase()}
                    </MonoMeta>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Invite link */}
      <section
        style={{
          marginTop: 26,
          padding: "18px 18px",
          borderRadius: 14,
          background: "var(--surface)",
          boxShadow: "0 0 0 1px var(--nav-border)",
        }}
      >
        <Eyebrow>{t.dashInviteEyebrow}</Eyebrow>
        {inviteCode ? (
          <>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 8,
                background: "var(--surface-2)",
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--ink)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {inviteHostShort}
              </span>
              <button
                type="button"
                onClick={copyInvite}
                className="uppercase"
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  background: "var(--ink)",
                  color: "var(--bg)",
                  border: "none",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  cursor: "pointer",
                }}
              >
                {copied ? t.dashInviteCopied : t.dashInviteCopy}
              </button>
            </div>
            <div style={{ marginTop: 8 }}>
              <MonoMeta>
                {t.dashInviteCodePrefix}
                {inviteCode}
              </MonoMeta>
            </div>
          </>
        ) : (
          <>
            <p style={{ marginTop: 6, fontSize: 13, color: "var(--ink-2)" }}>{t.dashInviteHint}</p>
            <button
              type="button"
              onClick={handleInvite}
              disabled={generating}
              className="uppercase disabled:opacity-50"
              style={{
                marginTop: 12,
                padding: "12px 16px",
                borderRadius: 10,
                background: "var(--ink)",
                color: "var(--bg)",
                border: "none",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                cursor: "pointer",
                width: "100%",
              }}
            >
              {generating ? t.dashInviteGenerating : t.dashInviteGenerate}
            </button>
          </>
        )}
        {error ? (
          <p style={{ marginTop: 8, fontSize: 11, color: "var(--bad-fg)" }}>{error}</p>
        ) : null}
      </section>

      {/* Admin panel — visible only when ADMIN_EMAILS contains user.email. */}
      {isAdmin && onOpenAdmin ? (
        <section style={{ marginTop: 26 }}>
          <Eyebrow>{t.dashAdminEyebrow}</Eyebrow>
          <button
            type="button"
            onClick={onOpenAdmin}
            className="w-full text-left"
            style={{
              marginTop: 10,
              padding: "14px 16px",
              borderRadius: 12,
              background: "var(--accent)",
              color: "var(--accent-ink)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 6px 20px var(--accent-glow)",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{t.dashAdminPanel}</div>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  opacity: 0.85,
                  marginTop: 2,
                }}
              >
                {t.dashAdminPanelSub}
              </div>
            </div>
            <IconChevR size={14} />
          </button>
        </section>
      ) : null}

      {/* Cel kaloryczny — entry point for /targets (otherwise unreachable). */}
      {onOpenTargets ? (
        <section style={{ marginTop: 26 }}>
          <Eyebrow>{t.dashYourDayEyebrow}</Eyebrow>
          <button
            type="button"
            onClick={onOpenTargets}
            className="w-full text-left"
            style={{
              marginTop: 10,
              padding: "14px 16px",
              borderRadius: 12,
              background: "var(--surface)",
              boxShadow: "0 0 0 1px var(--nav-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
                {t.dashCalorieGoal}
              </div>
              <MonoMeta>{t.dashCalorieGoalSub}</MonoMeta>
            </div>
            <IconChevR size={14} />
          </button>
        </section>
      ) : null}

      {/* Language */}
      <section style={{ marginTop: 26 }}>
        <Eyebrow>{t.dashLanguageEyebrow}</Eyebrow>
        <div className="flex gap-2" style={{ marginTop: 10 }}>
          {LANGS.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => {
                setLang(l);
                // Persist to the account; local change applies regardless.
                void api.setLanguage(l).catch(() => undefined);
              }}
              aria-pressed={l === lang}
              className="flex-1 text-[13px] font-bold"
              style={{
                padding: "12px 0",
                borderRadius: 12,
                fontFamily: "var(--font-mono)",
                background: l === lang ? "var(--ink)" : "var(--surface)",
                color: l === lang ? "var(--bg)" : "var(--ink-2)",
                boxShadow: l === lang ? "none" : "0 0 0 1px var(--nav-border)",
              }}
            >
              {LANG_LABEL[l]}
            </button>
          ))}
        </div>
      </section>

      {/* Family profiles (ADR-0018). Hidden when the feature flag is off. */}
      {childProfilesFlagOn && onOpenChildren ? (
        <section style={{ marginTop: 26 }}>
          <Eyebrow>{tChildren.chSectionEyebrow}</Eyebrow>
          <button
            type="button"
            onClick={onOpenChildren}
            className="w-full text-left"
            style={{
              marginTop: 10,
              padding: "14px 16px",
              borderRadius: 12,
              background: "var(--surface)",
              boxShadow: "0 0 0 1px var(--nav-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "none",
              cursor: "pointer",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
                {tChildren.chDashOpen}
              </div>
              <MonoMeta>{tChildren.chDashSectionHint(childProfiles.length).toUpperCase()}</MonoMeta>
            </div>
            <IconChevR size={14} />
          </button>
        </section>
      ) : null}

      {/* Appearance — ADHD Home mode (ADR-0015). Only when the flag is on. */}
      {authUser?.adhdModeAvailable ? (
        <section style={{ marginTop: 26 }}>
          <Eyebrow>{tAdhd.setEyebrow}</Eyebrow>
          <div
            style={{
              marginTop: 10,
              padding: "12px 16px",
              borderRadius: 12,
              background: "var(--surface)",
              boxShadow: "0 0 0 1px var(--nav-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
                {tAdhd.setEnableLabel}
              </div>
              <MonoMeta>{tAdhd.setEnableHint.toUpperCase()}</MonoMeta>
            </div>
            <div className="flex gap-2 shrink-0">
              {(
                [
                  [true, tAdhd.setOn],
                  [false, tAdhd.setOff],
                ] as const
              ).map(([on, label]) => {
                const active = (authUser.adhdEnabled ?? false) === on;
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      updateUser({ adhdEnabled: on });
                      void api.setAdhd(on, authUser.adhdVariant).catch(() => undefined);
                    }}
                    className="text-[12px] font-bold"
                    style={{
                      padding: "8px 14px",
                      borderRadius: 10,
                      fontFamily: "var(--font-mono)",
                      background: active ? "var(--ink)" : "var(--surface)",
                      color: active ? "var(--bg)" : "var(--ink-2)",
                      boxShadow: active ? "none" : "0 0 0 1px var(--nav-border)",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {authUser.adhdEnabled ? (
            <div style={{ marginTop: 10 }}>
              <Eyebrow>{tAdhd.setVariantLabel}</Eyebrow>
              <div className="flex gap-2" style={{ marginTop: 8 }}>
                {(
                  // Krok (variant C) chwilowo schowane z UI — patrz adhd-chooser.tsx.
                  [
                    ["A", tAdhd.variantA],
                    ["B", tAdhd.variantB],
                  ] as const
                ).map(([v, label]) => {
                  const active = authUser.adhdVariant === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        const next: AdhdVariant = v;
                        updateUser({ adhdVariant: next });
                        void api.setAdhd(true, next).catch(() => undefined);
                      }}
                      className="flex-1 text-[13px] font-bold"
                      style={{
                        padding: "12px 0",
                        borderRadius: 12,
                        fontFamily: "var(--font-mono)",
                        background: active ? "var(--ink)" : "var(--surface)",
                        color: active ? "var(--bg)" : "var(--ink-2)",
                        boxShadow: active ? "none" : "0 0 0 1px var(--nav-border)",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Shared data */}
      <section style={{ marginTop: 26 }}>
        <Eyebrow>{t.dashSharedDataEyebrow}</Eyebrow>
        <div style={{ marginTop: 10 }}>
          {(
            [
              [t.dashSharedFridge, t.dashSharedFridgeHint],
              [t.dashSharedList, t.dashSharedListHint],
              [t.dashSharedMeals, t.dashSharedMealsHint],
            ] as const
          ).map(([label, hint]) => (
            <div
              key={label}
              style={{
                padding: "12px 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: "1px solid var(--nav-border)",
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{label}</div>
                <MonoMeta>{hint.toUpperCase()}</MonoMeta>
              </div>
              <div style={{ color: "var(--muted)" }}>
                <IconChevR size={14} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* RODO: Twoje dane (PRESERVED) */}
      <section style={{ marginTop: 26 }}>
        <Eyebrow>{t.dashGdprEyebrow}</Eyebrow>
        <p style={{ marginTop: 6, fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
          {t.dashGdprBody}
        </p>
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="uppercase disabled:opacity-50"
            style={{
              padding: "12px 16px",
              borderRadius: 10,
              background: "transparent",
              color: "var(--ink)",
              boxShadow: "inset 0 0 0 1px var(--nav-border)",
              border: "none",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            {exporting ? t.dashExporting : t.dashExport}
          </button>
          <button
            type="button"
            onClick={handleDeleteAccount}
            disabled={deleting}
            className="uppercase disabled:opacity-50"
            style={{
              padding: "12px 16px",
              borderRadius: 10,
              background: "transparent",
              color: "var(--bad-fg)",
              boxShadow: "inset 0 0 0 1px var(--bad-fg)",
              border: "none",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            {deleting ? t.dashDeleting : t.dashDeleteAccount}
          </button>
        </div>
        {accountError ? (
          <p style={{ marginTop: 8, fontSize: 11, color: "var(--bad-fg)" }}>{accountError}</p>
        ) : null}
      </section>

      {/* Footer links: Privacy / Terms (PRESERVED) */}
      <footer
        style={{
          marginTop: 28,
          paddingTop: 16,
          display: "flex",
          justifyContent: "center",
          gap: 18,
          borderTop: "1px solid var(--nav-border)",
        }}
      >
        {onOpenPrivacy ? (
          <button
            type="button"
            onClick={onOpenPrivacy}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              fontSize: 11,
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            {t.dashFooterPrivacy}
          </button>
        ) : null}
        {onOpenTerms ? (
          <button
            type="button"
            onClick={onOpenTerms}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              fontSize: 11,
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            {t.dashFooterTerms}
          </button>
        ) : null}
      </footer>
    </ScreenShell>
  );
}
